import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Delete as DeleteIcon, CheckCircle as ApproveIcon, Cancel as RejectIcon } from '@mui/icons-material';
import { updateUserRoleDirect, updateUserStatusDirect, deleteUserDirect } from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import type { User, UserRole } from '@timetable/types';
import { ConfirmationDialog } from '../shared/ConfirmationDialog';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function sendStatusEmail(userId: string, status: 'approved' | 'rejected') {
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/send-approval-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ userId, status }),
    });
  } catch (err) {
    console.warn('[sendStatusEmail] Failed:', err);
  }
}

interface UserListProps {
  users?: User[];
  isLoading?: boolean;
}

/**
 * UserList Component
 * 
 * Displays users in a table format with:
 * - User details (name, email, school, role, status)
 * - Role dropdown for promote/demote
 * - Delete button with confirmation
 * - Pagination support (100 per page)
 * 
 * Requirements: 2.4.2, 2.4.3, 2.4.4, 2.4.5, 3.4.2, 3.4.3, 4.1.3
 */
export function UserList({ users, isLoading }: UserListProps) {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [roleChangeDialogOpen, setRoleChangeDialogOpen] = useState(false);
  const [roleChangeData, setRoleChangeData] = useState<{
    user: User;
    newRole: UserRole;
  } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Update user status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: 'approved' | 'rejected' | 'inactive' | 'pending' }) =>
      updateUserStatusDirect(userId, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      if (variables.status === 'approved' || variables.status === 'rejected') {
        sendStatusEmail(variables.userId, variables.status);
      }
    },
  });

  // Update user role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      updateUserRoleDirect(userId, role as 'admin' | 'standard' | 'teacher'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      setRoleChangeDialogOpen(false);
      setRoleChangeData(null);
    },
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => deleteUserDirect(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
  });

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  const handleRoleChange = (user: User, event: SelectChangeEvent) => {
    const newRole = event.target.value as UserRole;
    // Don't allow changing to super_admin
    if (newRole === 'super_admin') return;
    
    // Show confirmation dialog for role changes
    setRoleChangeData({ user, newRole });
    setRoleChangeDialogOpen(true);
  };

  const handleRoleChangeConfirm = () => {
    if (roleChangeData) {
      updateRoleMutation.mutate({
        userId: roleChangeData.user.id,
        role: roleChangeData.newRole,
      });
    }
  };

  const handleApproveUser = (user: User) => {
    updateStatusMutation.mutate({
      userId: user.id,
      status: 'approved',
    });
  };

  const handleRejectUser = (user: User) => {
    updateStatusMutation.mutate({
      userId: user.id,
      status: 'rejected',
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
      case 'removed':
        return 'error';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'admin':
        return 'primary';
      case 'teacher':
        return 'info';
      case 'standard':
        return 'default';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading users...</Typography>
      </Box>
    );
  }

  if (!users || users.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No users found matching the selected filters.
        </Typography>
      </Box>
    );
  }

  // Paginate users
  const paginatedUsers = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>School</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {user.name}
                  </Typography>
                  {user.phone_number && (
                    <Typography variant="caption" color="text.secondary">
                      {user.phone_number}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{user.email}</Typography>
                </TableCell>
                <TableCell>
                  {user.school_id ? (
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontSize="0.75rem"
                    >
                      {user.school_id}
                    </Typography>
                  ) : (
                    <Chip
                      label="Super Admin"
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {user.role === 'super_admin' ? (
                    <Chip
                      label="Super Admin"
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  ) : (
                    <Select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user, e)}
                      size="small"
                      disabled={updateRoleMutation.isPending}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="teacher">Teacher</MenuItem>
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.status}
                    color={getStatusColor(user.status)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  {user.status === 'pending' && (
                    <>
                      <Tooltip title="Approve user">
                        <IconButton
                          size="small"
                          onClick={() => handleApproveUser(user)}
                          color="success"
                          disabled={updateStatusMutation.isPending}
                        >
                          <ApproveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject user">
                        <IconButton
                          size="small"
                          onClick={() => handleRejectUser(user)}
                          color="warning"
                          disabled={updateStatusMutation.isPending}
                        >
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Delete user">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(user)}
                      color="error"
                      disabled={user.role === 'super_admin'}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete ${userToDelete?.name} (${userToDelete?.email})? The user will be soft-deleted (status set to 'removed') and will no longer be able to access the system. This action preserves the user data for audit purposes.`}
        confirmText="Delete User"
        isDestructive
        isLoading={deleteMutation.isPending}
        error={deleteMutation.error as Error | null}
        warningMessage="This action will mark the user as removed!"
      />

      {/* Role Change Confirmation Dialog */}
      <ConfirmationDialog
        open={roleChangeDialogOpen}
        onClose={() => {
          setRoleChangeDialogOpen(false);
          setRoleChangeData(null);
        }}
        onConfirm={handleRoleChangeConfirm}
        title="Change User Role"
        message={`Are you sure you want to change ${roleChangeData?.user.name}'s role from ${roleChangeData?.user.role} to ${roleChangeData?.newRole}? This will affect their permissions and access to system features.`}
        confirmText="Change Role"
        isDestructive={roleChangeData?.newRole === 'standard' || roleChangeData?.newRole === 'teacher'}
        isLoading={updateRoleMutation.isPending}
        error={updateRoleMutation.error as Error | null}
      />
    </>
  );
}
